<template>
    <div class="modal fade" id="setPagePasswordModal" ref="setPagePasswordModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-md">
            <div class="modal-content">
                <div class="modal-header tw-red-bg-color text-white">
                    <div class="d-flex">
                        <span class="tw-create-modal-icon d-inline-flex justify-content-center align-items-center tw-light-red-bg-color me-3">
                            <i class="fas fa-lock"></i>
                        </span>
                        <span class="d-inline-flex">
                            <div class="row">
                                <h5 class="modal-title col-12" id="modal-title">{{ hasPassword ? 'Change' : 'Set' }} Page Password</h5>
                                <p class="col-12 mb-0">{{ hasPassword ? 'Change or remove' : 'Set' }} password protection for this page</p>
                            </div>
                        </span>
                    </div>
                </div>

                <form @submit.prevent.stop="setPassword">
                    <div class="modal-body" id="modal-body">
                        <div v-show="sendError" class="alert alert-danger" role="alert">{{ errorMessage }}</div>
                        <div v-show="sendSuccess" class="alert alert-success" role="alert">{{ successMessage }}</div>

                        <div class="row">
                            <div class="col">
                                <div class="form-group mb-4">
                                    <label for="page-password" class="mb-2">Password</label>
                                    <input
                                        type="password"
                                        class="form-control"
                                        v-model="password"
                                        required
                                        id="page-password"
                                        placeholder="Enter password (minimum 6 characters)"
                                        minlength="6"
                                    >
                                    <small class="form-text text-muted">Minimum 6 characters required</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer d-flex justify-content-between">
                        <button class="btn tw-dark-grey-bg-color text-white" type="button" @click="closeModal">Cancel</button>
                        <div class="d-flex gap-2">
                            <button
                                v-if="hasPassword"
                                class="btn btn-warning text-white"
                                type="button"
                                @click="removePassword"
                                :disabled="spinner"
                                style="min-width: 150px;"
                                title="Remove Password"
                            >
                                Remove
                                <span v-if="spinner"><i class="fas fa-spinner fa-spin"></i></span>
                            </button>
                            <button class="btn tw-red-bg-color text-white" type="submit" :disabled="spinner" style="min-width: 150px;" :title="hasPassword ? 'Update Password' : 'Set Password'">
                                {{ hasPassword ? 'Update' : 'Set' }}
                                <span v-if="spinner"><i class="fas fa-spinner fa-spin"></i></span>
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { Modal } from 'bootstrap';
import cms6Mixin from '@/mixins/cms6Mixin';

export default {
    name: 'SetPagePasswordModal',
    mixins: [cms6Mixin],
    data: () => ({
        modal: null,
        pageId: null,
        password: '',
        hasPassword: false,
        spinner: false,
        sendError: false,
        sendSuccess: false,
        errorMessage: '',
        successMessage: '',
    }),
    methods: {
        showModal(pageId, hasPassword = false) {
            this.pageId = pageId;
            this.hasPassword = hasPassword;
            this.password = '';
            this.sendError = false;
            this.sendSuccess = false;
            this.errorMessage = '';
            this.successMessage = '';

            if (!this.modal) {
                this.modal = new Modal(this.$refs.setPagePasswordModal);
            }

            this.modal.show();
        },

        closeModal() {
            if (this.modal) {
                this.modal.hide();
            }
        },

        async setPassword() {
            if (!this.password || this.password.length < 6) {
                this.sendError = true;
                this.errorMessage = 'Password must be at least 6 characters long';
                return;
            }

            this.spinner = true;
            this.sendError = false;
            this.sendSuccess = false;

            try {
                const response = await this.setPagePassword(this.pageId, this.password);

                if (response && response.status === 200) {
                    this.sendSuccess = true;
                    this.successMessage = 'Password set successfully';
                    this.password = '';

                    setTimeout(() => {
                        this.closeModal();
                        this.$emit('passwordUpdated');
                    }, 800);
                } else {
                    this.sendError = true;
                    this.errorMessage = 'Failed to set password';
                }
            } catch (error) {
                this.sendError = true;
                this.errorMessage = error.response?.data?.message || 'An error occurred';
            } finally {
                this.spinner = false;
            }
        },

        async removePassword() {
            if (!confirm('Are you sure you want to remove password protection from this page?')) {
                return;
            }

            this.spinner = true;
            this.sendError = false;
            this.sendSuccess = false;

            try {
                const response = await this.removePagePassword(this.pageId);

                if (response && response.status === 200) {
                    this.sendSuccess = true;
                    this.successMessage = 'Password removed successfully';

                    setTimeout(() => {
                        this.closeModal();
                        this.$emit('passwordUpdated');
                    }, 800);
                } else {
                    this.sendError = true;
                    this.errorMessage = 'Failed to remove password';
                }
            } catch (error) {
                this.sendError = true;
                this.errorMessage = error.response?.data?.message || 'An error occurred';
            } finally {
                this.spinner = false;
            }
        },
    },
};
</script>

<style scoped>
.tw-create-modal-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
}
</style>
